/** @typedef {import('headlight-configurable-application').Pict} Pict */

// Shared Headlight behavior for the pict-section-form complex-editor input types (Diagram, RichText).
//
// PSF ships these editors booting in VIEW mode with no edit affordance — the host must drive setMode.
// The Diagram and RichText providers are structurally identical (same setMode/getMode/commit API, same
// _instances-keyed-by-Hash, same #DISPLAY-FOR-{RawHTMLID} slot), differing only in their base class and
// provider identifier, so this factory adds the Headlight presentation behavior once and both providers
// reuse it. The presentation is a single inline one: the field edits in place, and a View/Edit segmented
// switch (setEditorMode) flips between the static view and the live editor, toggling the `is-editing`
// class the CSS keys off. Switching to View commits first (a final synchronous export so a quick
// edit-then-View does not drop the last change).
//
// The field opens and closes through that switch ON PURPOSE. Closing is the commit point, so an
// "always editing" presentation would be a field with no moment at which its value is known to be
// exported — that mode was considered and dropped rather than carry an unreliable save path. (A
// floating "Compact" modal presentation also existed and was removed 2026-07-22: the inline editor
// carries its own toolbar, so the modal added bug surface — backdrop, centering, scroll containment,
// preview sizing — without a need it earned.)
//
// Tabular is intentionally unsupported — the PSF base throws in onInputInitializeTabular (N editors per
// row is a performance landmine), so these types are Standard/Vertical (and RecordSet-as-Tabular) only.

const libEditorCSS = require('./PictProvider-InputType-Editor-CSS.js');

/**
 * Build a Headlight subclass of a PSF complex-editor input provider.
 *
 * @param {any} pBaseClass - The PSF provider class (DiagramInput / RichTextInput).
 * @param {Record<string, any>} pDefaultConfiguration - Provider configuration (ProviderIdentifier etc.).
 * @return {any} The Headlight subclass.
 */
function buildEditorInputProvider(pBaseClass, pDefaultConfiguration)
{
	class HeadlightEditorInput extends pBaseClass
	{
		/**
		 * @param {import('fable')} pFable @param {Record<string, any>} [pOptions] @param {string} [pServiceHash]
		 */
		constructor(pFable, pOptions, pServiceHash)
		{
			super(pFable, Object.assign({}, pDefaultConfiguration, pOptions), pServiceHash);
			/** @type {Pict} */
			this.pict;
		}

		/**
		 * The id of the Headlight presentation container the templates wrap the field in. The PSF base
		 * only toggles mode classes on the hidden input / display slot, so the View/Edit switch needs its
		 * own container hook (the `is-editing` class), which this owns.
		 * @param {string} pRawHTMLID @return {string}
		 */
		getEditorContainerID(pRawHTMLID)
		{
			return `PICT-EDITOR-FOR-${pRawHTMLID}`;
		}

		/**
		 * Reflect edit/view state on the presentation container so the switch highlight and the
		 * view/editor swap respond in CSS.
		 * @param {string} pRawHTMLID @param {string} pMode - 'edit' | 'view'
		 */
		_reflectEditorState(pRawHTMLID, pMode)
		{
			if (typeof document === 'undefined')
			{
				return;
			}
			const tmpContainer = document.getElementById(this.getEditorContainerID(pRawHTMLID));
			if (tmpContainer && tmpContainer.classList)
			{
				tmpContainer.classList.toggle('is-editing', pMode === 'edit');
			}
		}

		/** One-time registration of the Headlight wrapper CSS (the PSF provider injects its own editor CSS separately). */
		_injectEditorCSS()
		{
			if (this._editorCSSInjected || !this.pict || !this.pict.CSSMap || (typeof this.pict.CSSMap.addCSS !== 'function'))
			{
				return;
			}
			this._editorCSSInjected = true;
			this.pict.CSSMap.addCSS('Headlight-Editor-Inputs', libEditorCSS, 500);
			if (typeof this.pict.CSSMap.injectCSS === 'function')
			{
				this.pict.CSSMap.injectCSS();
			}
		}

		onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
		{
			this._injectEditorCSS();
			return super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
		}

		/**
		 * While an editor is OPEN, never let a marshal push content back into it — the open editor is
		 * the authority on its own content.
		 *
		 * Upstream, both PSF providers treat edit mode as "hot-replace what the user is working on":
		 * RichText re-renders its view (which destroys and rebuilds the CodeMirror EditorView) and
		 * Diagram calls setScene() on the live canvas. That fires on the field's OWN export, so every
		 * keystroke / tool click loops:
		 *     editor change -> _writeHiddenInputValue dispatches 'change' -> form marshals every section
		 *     -> onDataMarshalToForm hits this still-open editor -> it replaces itself mid-edit.
		 * Symptoms were RichText losing focus on the first keypress (unusable) and the Diagram canvas
		 * blanking on any tool click — the marshaled value there is the descriptor's hand-written
		 * placeholder SVG, which carries no embedded Excalidraw scene, so the replacement scene is empty.
		 *
		 * Skipping is safe in both directions: the echo case is a value the editor just produced, and a
		 * genuine external write (a solver) would be hostile mid-edit anyway — it would silently discard
		 * what the user is typing. Closing the editor commits, so the user's content wins on the way out.
		 * View mode still delegates, because re-mounting the static view from a new value IS correct.
		 * The base InputExtension.onDataMarshalToForm is a `return true` no-op, so returning true here
		 * matches the contract exactly.
		 *
		 * @param {Object} pView @param {Object} pGroup @param {Object} pRow @param {Object} pInput
		 * @param {any} pValue @param {string} pHTMLSelector @param {string} pTransactionGUID
		 * @return {any}
		 */
		onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
		{
			const tmpInstance = (this._instances && pInput) ? this._instances[pInput.Hash] : null;
			if (tmpInstance && (tmpInstance.mode === 'edit'))
			{
				return true;
			}
			return super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
		}

		/**
		 * Defer the per-keystroke whole-form marshal to the View (accept) step.
		 *
		 * PSF writes the editor's value into the hidden <input> on every keystroke (RichText) / scene
		 * change (Diagram), and each write dispatches a 'change' that runs the form's WHOLE-form marshal —
		 * every section re-marshals on every keystroke (other providers' onDataMarshalToForm all fire; it
		 * shows up as their console logs and is pure overhead for a display field nothing else reads live).
		 * The value write is cheap and worth keeping (the hidden input stays current and informary-bound,
		 * so a save that re-reads fields still sees the latest); the 'change' DISPATCH is the expensive
		 * part. So while a field is being edited we keep the value write but SUPPRESS the dispatch. The one
		 * marshal then fires on toggle to View — the natural "accept" point before a document save (see
		 * setEditorMode → _flushMarshal). Diagram's OnChange is already throttled (150ms); this collapses
		 * both editors to a single marshal per edit session.
		 * @param {string} pInputHTMLID @param {any} pValue @return {boolean}
		 */
		_writeHiddenInputValue(pInputHTMLID, pValue)
		{
			if ((typeof document !== 'undefined') && this._isEditingRawHTMLID(pInputHTMLID))
			{
				const tmpElement = /** @type {HTMLInputElement} */ (document.getElementById(pInputHTMLID));
				if (!tmpElement)
				{
					return false;
				}
				// Keep the bound value current, but do NOT dispatch 'change' — the marshal waits for View.
				tmpElement.value = (pValue == null) ? '' : String(pValue);
				return true;
			}
			return super._writeHiddenInputValue(pInputHTMLID, pValue);
		}

		/**
		 * @param {string} pRawHTMLID @return {boolean} True if an instance with this RawHTMLID is editing.
		 */
		_isEditingRawHTMLID(pRawHTMLID)
		{
			if (!this._instances)
			{
				return false;
			}
			for (const tmpHash of Object.keys(this._instances))
			{
				const tmpInstance = this._instances[tmpHash];
				if (tmpInstance && (tmpInstance.mode === 'edit') && tmpInstance.input && tmpInstance.input.Macro &&
					(tmpInstance.input.Macro.RawHTMLID === pRawHTMLID))
				{
					return true;
				}
			}
			return false;
		}

		/**
		 * Dispatch one 'change' on the hidden input so its (already-current) value marshals a single time.
		 * Used both on View and at save time. The value is already in the bound hidden input; the 'change'
		 * makes the form read it into FormData (and propagate). The onDataMarshalToForm guard keeps this
		 * from disturbing a still-open editor.
		 * @param {string} pRawHTMLID
		 */
		/**
		 * The open editor's own latest content, for providers that track it; undefined when they don't.
		 * RichText overrides this (PSF keeps it as `lastValue` on every keystroke). Diagram does not: its
		 * value comes from a forced export at commit, so re-asserting a remembered value could clobber it.
		 * @param {Record<string, any>} pInstance @return {string|undefined}
		 */
		_getOpenEditorValue(pInstance)
		{
			return undefined;
		}

		/**
		 * Put an open editor's own content back into its hidden input before that input is flushed.
		 *
		 * The hidden input is the only place a typed value lives until it marshals, and the form writes
		 * FormData back into EVERY input on a whole-form marshal -- the provider's onDataMarshalToForm skip
		 * above cannot stop that. So with two editors open, the first one's flush reset the second one's
		 * hidden input to its stale saved value, and the second flush then saved the stale value: every
		 * open remark but one was lost on Save (Matt, LADOTD F&E forms, 2026-09-24).
		 * @param {Record<string, any>} pInstance
		 */
		_reassertHiddenInputValue(pInstance)
		{
			const tmpValue = this._getOpenEditorValue(pInstance);
			if ((typeof tmpValue !== 'string') || (typeof document === 'undefined') || !pInstance || !pInstance.input || !pInstance.input.Macro)
			{
				return;
			}
			const tmpElement = /** @type {HTMLInputElement} */ (document.getElementById(pInstance.input.Macro.RawHTMLID));
			if (tmpElement)
			{
				tmpElement.value = tmpValue;
			}
		}

		_flushMarshal(pRawHTMLID)
		{
			if (typeof document === 'undefined')
			{
				return;
			}
			const tmpElement = document.getElementById(pRawHTMLID);
			if (tmpElement && (typeof tmpElement.dispatchEvent === 'function'))
			{
				try
				{
					tmpElement.dispatchEvent(new Event('change', { bubbles: true }));
				}
				catch (pError)
				{
					// jsdom-in-test sometimes lacks the Event constructor; harmless in the browser.
				}
			}
		}

		/**
		 * Save-lifecycle hook — flush any editor still OPEN when the document is saved.
		 *
		 * The per-keystroke marshal is deferred to View (see _writeHiddenInputValue), and the app's save
		 * path reads the in-memory FormData without re-reading the DOM — so a Save fired while an editor is
		 * open would persist the PRE-edit value (the typed value is only in the bound hidden input, not yet
		 * in FormData). Here we flush each open editor first: the value is already in its hidden input, so
		 * one 'change' reads it into FormData before save proceeds. Requires AutoSaveDataWithApp:true in the
		 * provider config, or this hook never runs. Closed editors are already current and are skipped.
		 * @param {(pError?: Error) => void} fCallback
		 */
		onBeforeSaveDataAsync(fCallback)
		{
			if (this._instances)
			{
				for (const tmpHash of Object.keys(this._instances))
				{
					const tmpInstance = this._instances[tmpHash];
					if (tmpInstance && (tmpInstance.mode === 'edit') && tmpInstance.input && tmpInstance.input.Macro)
					{
						// An earlier editor's flush may have reset this one's hidden input -- see above.
						this._reassertHiddenInputValue(tmpInstance);
						this._flushMarshal(tmpInstance.input.Macro.RawHTMLID);
					}
				}
			}
			return super.onBeforeSaveDataAsync(fCallback);
		}

		/**
		 * Move a field to an explicit mode. Driven by the View/Edit segmented switch, where each segment
		 * names its target mode rather than flipping whatever is current.
		 * @param {string} pInputHash @param {string} pRawHTMLID @param {string} pMode - 'edit' | 'view'
		 */
		setEditorMode(pInputHash, pRawHTMLID, pMode)
		{
			if (this.getMode(pInputHash) === pMode)
			{
				return;
			}
			if (pMode === 'view')
			{
				// Commit (Diagram forces a final export; RichText already has the value from keystrokes),
				// drop to view, THEN marshal once. That single flush is the deferred, accept-time marshal
				// of everything done while editing — see _writeHiddenInputValue for why per-keystroke
				// marshals are suppressed. Flushing after setMode means the field is in view mode, so the
				// dispatch is not re-suppressed and onDataMarshalToForm delegates normally.
				this.commit(pInputHash, () =>
				{
					this.setMode(pInputHash, 'view', () =>
					{
						this._reflectEditorState(pRawHTMLID, 'view');
						// Any whole-form marshal while this was open (another editor's View, a picker change)
						// reset its hidden input to the saved value; put the typed content back first.
						this._reassertHiddenInputValue(this._instances ? this._instances[pInputHash] : null);
						this._flushMarshal(pRawHTMLID);
					});
				});
				return;
			}
			this.setMode(pInputHash, 'edit', (pError) =>
			{
				this._reflectEditorState(pRawHTMLID, 'edit');
				if (!pError)
				{
					this._focusEditor(pRawHTMLID);
				}
			});
		}

		/**
		 * Put the caret in a just-opened editor so the user can type without another click. Called only
		 * from the Edit switch, after the editor has rendered, so a remount never steals focus.
		 * @param {string} pRawHTMLID
		 */
		_focusEditor(pRawHTMLID)
		{
		}
	}
	return HeadlightEditorInput;
}

module.exports = { buildEditorInputProvider };
